import React from 'react';
import WOWO from '../../lib/wowo';
import {
    ReadinessCard,
    CPUUtilizationCard,
    RAMUtilizationCard,
    StorageUtilizationCard,
} from '../Card/NewInventoryCard';
import { InventoryCard } from '../Card';
import {
    formatStorageSize,
    calcReadyForMigration,
    deriveNetTrend,
    deriveRowTrend,
} from './inventorySummaryHelpers';
import { getGiBfromMiB } from '../../lib/helpers';
import { formatCount, formatDecimal } from '../../pages/Discovery/helpers';

/**
 * Renders the inventory cards grid contents.
 * When the new_summary_cards flag is on, renders the four utilization cards.
 * Otherwise renders the legacy InventoryCard set.
 */
export const InventoryCardsSection = ({
    summaryData,
    inventoryTotals,
    isExpanded,
    showCardEllipsis,
    onIgnoreOptimizations,
    onFilterClick,
}) => {
    const { totalInventory } = inventoryTotals;
    const totalCPU = summaryData?.total_cpu ?? 0;
    const totalRAM = summaryData?.total_ram
        ? formatStorageSize(Math.floor(getGiBfromMiB(summaryData.total_ram)))
        : { value: 0, unit: 'GiB' };
    const totalStorage = summaryData?.total_storage
        ? formatStorageSize(Math.floor(getGiBfromMiB(summaryData.total_storage)))
        : { value: 0, unit: 'GiB' };

    const readyForMigration = calcReadyForMigration(summaryData?.ready);
    const rawPercent = totalInventory > 0 ? (readyForMigration / totalInventory) * 100 : 0;
    const percentReady = (rawPercent > 0 && rawPercent < 1) ? 1 : Math.floor(rawPercent);

    const undersizedCPU = summaryData?.undersized_by_cpu ?? 0;
    const undersizedRAM = summaryData?.undersized_by_ram ?? 0;
    const cpuReductionOpportunities = summaryData?.oversized_by_cpu ?? 0;
    const ramReductionOpportunities = summaryData?.oversized_by_ram ?? 0;
    const undersizedStorage = summaryData?.undersized_by_storage ?? 0;
    const storageReductionOpportunities = summaryData?.oversized_by_storage ?? 0;

    const optimizedByCPU = Math.max(0, totalInventory - cpuReductionOpportunities - undersizedCPU);
    const optimizedByRAM = Math.max(0, totalInventory - ramReductionOpportunities - undersizedRAM);
    const optimizedByStorage = Math.max(0, totalInventory - storageReductionOpportunities - undersizedStorage);

    const totalOversizedVMs = summaryData?.oversized;
    const totalUndersizedVMs = summaryData?.undersized;

    const cpuOversizedTrend = summaryData?.cpu_oversized_trend ?? 0;
    const cpuUndersizedTrend = summaryData?.cpu_undersized_trend ?? 0;
    const ramOversizedTrend = summaryData?.ram_oversized_trend ?? 0;
    const ramUndersizedTrend = summaryData?.ram_undersized_trend ?? 0;
    const storageOversizedTrend = summaryData?.storage_oversized_trend ?? 0;
    const storageUndersizedTrend = summaryData?.storage_undersized_trend ?? 0;

    const cpuPrimaryTrend = deriveNetTrend(cpuOversizedTrend, cpuUndersizedTrend);
    const ramPrimaryTrend = deriveNetTrend(ramOversizedTrend, ramUndersizedTrend);
    const storagePrimaryTrend = deriveNetTrend(storageOversizedTrend, storageUndersizedTrend);
    const cpuOversizedRowTrend = deriveRowTrend(cpuOversizedTrend);
    const cpuUndersizedRowTrend = deriveRowTrend(cpuUndersizedTrend);
    const ramOversizedRowTrend = deriveRowTrend(ramOversizedTrend);
    const ramUndersizedRowTrend = deriveRowTrend(ramUndersizedTrend);
    const storageOversizedRowTrend = deriveRowTrend(storageOversizedTrend);
    const storageUndersizedRowTrend = deriveRowTrend(storageUndersizedTrend);

    if (WOWO.isEnabled('new_summary_cards')) {
        return (
            <>
                <ReadinessCard
                    readyForMigration={readyForMigration}
                    percentReady={percentReady}
                    totalInventory={totalInventory}
                    totalOversizedVMs={totalOversizedVMs}
                    totalUndersizedVMs={totalUndersizedVMs}
                    isExpanded={isExpanded}
                    onFilterClick={onFilterClick}
                />
                <CPUUtilizationCard
                    optimizedByCPU={optimizedByCPU}
                    totalInventory={totalInventory}
                    totalCPU={totalCPU}
                    cpuReductionOpportunities={cpuReductionOpportunities}
                    undersizedCPU={undersizedCPU}
                    primaryTrend={cpuPrimaryTrend}
                    oversizedTrend={cpuOversizedRowTrend}
                    undersizedTrend={cpuUndersizedRowTrend}
                    isExpanded={isExpanded}
                    onFilterClick={onFilterClick}
                />
                <RAMUtilizationCard
                    optimizedByRAM={optimizedByRAM}
                    totalInventory={totalInventory}
                    totalRAM={totalRAM}
                    ramReductionOpportunities={ramReductionOpportunities}
                    undersizedRAM={undersizedRAM}
                    primaryTrend={ramPrimaryTrend}
                    oversizedTrend={ramOversizedRowTrend}
                    undersizedTrend={ramUndersizedRowTrend}
                    isExpanded={isExpanded}
                    onFilterClick={onFilterClick}
                />
                <StorageUtilizationCard
                    optimizedByStorage={optimizedByStorage}
                    totalInventory={totalInventory}
                    totalStorage={totalStorage}
                    storageReductionOpportunities={storageReductionOpportunities}
                    undersizedStorage={undersizedStorage}
                    primaryTrend={storagePrimaryTrend}
                    oversizedTrend={storageOversizedRowTrend}
                    undersizedTrend={storageUndersizedRowTrend}
                    isExpanded={isExpanded}
                />
            </>
        );
    }

    const neededCores = summaryData?.total_needed_cpu;
    const neededRAMGiB = summaryData?.total_needed_ram ? Math.ceil(getGiBfromMiB(summaryData.total_needed_ram)) : 0;
    const neededRAM = formatStorageSize(neededRAMGiB);
    const potentialCPUSavings = summaryData?.total_unused_cpu;
    const potentialRAMSavingsGiB = summaryData?.total_unused_ram
        ? Math.floor(getGiBfromMiB(summaryData.total_unused_ram))
        : 0;
    const potentialRAMSavings = formatStorageSize(potentialRAMSavingsGiB);
    const osmOpportunities = summaryData?.can_modernize_os
        ? Object.values(summaryData.can_modernize_os).reduce((sum, val) => sum + val, 0)
        : 0;
    const windowsMachines = summaryData?.can_modernize_os?.windows ?? 0;
    const linuxMachines = summaryData?.can_modernize_os?.linux ?? 0;

    return (
        <>
            <InventoryCard
                showEllipsis={showCardEllipsis}
                key="ready-for-migration"
                title="Ready for Migration"
                content={[
                    { text: formatCount(readyForMigration), type: 'primary', continuation: `/${formatCount(totalInventory)}` },
                    { text: `Percent Ready: ${percentReady}%`, type: 'muted' },
                ]}
                variant="ready"
                backgroundFill={true}
                isExpanded={isExpanded}
            />
            <InventoryCard
                showEllipsis={showCardEllipsis}
                key="osm-opportunities"
                title="OSM Opportunities"
                onIgnoreOptimizations={() => onIgnoreOptimizations({ modernization: ['osm'] })}
                content={[
                    { text: formatCount(osmOpportunities), type: 'primary' },
                    { text: `Windows: ${formatCount(windowsMachines)}`, type: 'muted', inline: true },
                    { text: `Linux: ${formatCount(linuxMachines)}`, type: 'muted', inline: true },
                ]}
                isExpanded={isExpanded}
            />
            <InventoryCard
                showEllipsis={showCardEllipsis}
                key="undersized-cpu"
                title="Undersized by CPU"
                onIgnoreOptimizations={() => onIgnoreOptimizations({ cpu: ['undersized'] })}
                content={[
                    { text: formatCount(undersizedCPU), type: 'primary' },
                    { text: `Needed vCPUs: ${formatCount(neededCores)}`, type: 'muted', inline: true },
                ]}
                variant="notReady"
                isExpanded={isExpanded}
            />
            <InventoryCard
                showEllipsis={showCardEllipsis}
                key="undersized-ram"
                title="Undersized by RAM"
                onIgnoreOptimizations={() => onIgnoreOptimizations({ ram: ['undersized'] })}
                content={[
                    { text: formatCount(undersizedRAM), type: 'primary' },
                    { text: `Needed RAM: ${formatDecimal(neededRAM.value)} ${neededRAM.unit}`, type: 'muted', inline: true },
                ]}
                variant="notReady"
                isExpanded={isExpanded}
            />
            <InventoryCard
                showEllipsis={showCardEllipsis}
                key="oversized-cpu"
                title="Oversized by CPU"
                onIgnoreOptimizations={() => onIgnoreOptimizations({ cpu: ['oversized'] })}
                content={[
                    { text: formatCount(cpuReductionOpportunities), type: 'primary' },
                    { text: `Potential Savings: ${formatCount(potentialCPUSavings)} vCPUs`, type: 'muted' },
                ]}
                variant="notReady"
                isExpanded={isExpanded}
            />
            <InventoryCard
                showEllipsis={showCardEllipsis}
                key="oversized-ram"
                title="Oversized by RAM"
                onIgnoreOptimizations={() => onIgnoreOptimizations({ ram: ['oversized'] })}
                content={[
                    { text: formatCount(ramReductionOpportunities), type: 'primary' },
                    {
                        text: `Potential Savings: ${formatDecimal(potentialRAMSavings.value)} ${potentialRAMSavings.unit}`,
                        type: 'muted',
                    },
                ]}
                variant="notReady"
                isExpanded={isExpanded}
            />
        </>
    );
};
